import { useNativeStatusBarOffset } from "@/hooks/useNativeStatusBarOffset";
import { isFreshScope } from "@/lib/freshScope";
import { WebBillingOnly } from "@/components/account/WebBillingOnly";
import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { BottomNavigation } from "@/components/BottomNavigation";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { CookieConsent } from "@/components/CookieConsent";
import { PushNavigationBridge } from "@/components/PushNavigationBridge";
import { AttachmentPreviewHost } from "@/components/messages/attachmentPreviewStore";
import { TrackingProvider } from "@/components/TrackingProvider";
import { StickyMobileCTA } from "@/components/StickyMobileCTA";
const ThankYou = lazy(() => import("./pages/ThankYou"));
import { QuoteBasketProvider } from "@/contexts/QuoteBasketContext";
import { QuoteBasketBar } from "@/components/venue/QuoteBasketBar";
import { BusinessProtectedRoute } from "@/components/BusinessProtectedRoute";
import { AdminProtectedRoute } from "@/components/AdminProtectedRoute";
import { useCapacitorApp, getAppType } from "@/hooks/useCapacitorApp";
import { NativeHandoffBrowserGuard } from "@/components/auth/NativeHandoffBrowserGuard";
import { isNativeHandoffBrowser } from "@/lib/nativeHandoffBrowser";
import { ScrollProgress } from "@/components/animations/ScrollProgress";
import { BackToTop } from "@/components/animations/BackToTop";

// Lazy-load Home too - it's a large landing page with many heavy imports
const Home = lazy(() => import("./pages/Home").then(m => ({ default: m.Home })));

// Lazy-load all other pages
const Auth = lazy(() => import("./pages/Auth").then(m => ({ default: m.Auth })));
const InstagramDone = lazy(() => import("./pages/InstagramDone"));
const NativeAuthCallback = lazy(() => import("./pages/auth/NativeAuthCallback"));
const NativeAuthStart = lazy(() => import("./pages/auth/NativeAuthStart"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const ResetPassword = lazy(() => import("./pages/ResetPassword").then(m => ({ default: m.ResetPassword })));
const Directory = lazy(() => import("./pages/Directory").then(m => ({ default: m.Directory })));
const Blog = lazy(() => import("./pages/Blog").then(m => ({ default: m.Blog })));
const Contact = lazy(() => import("./pages/Contact").then(m => ({ default: m.Contact })));
const About = lazy(() => import("./pages/About").then(m => ({ default: m.About })));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Help = lazy(() => import("./pages/Help").then(m => ({ default: m.Help })));
const SupplierPricingPage = lazy(() => import("./pages/SupplierPricingPage"));
const SupplierProfile = lazy(() => import("./pages/SupplierProfile").then(m => ({ default: m.SupplierProfile })));
const InspirationPage = lazy(() => import("./pages/InspirationPage").then(m => ({ default: m.InspirationPage })));
const SubmitWeddingPage = lazy(() => import("./pages/SubmitWeddingPage").then(m => ({ default: m.SubmitWeddingPage })));
const RealWeddingPage = lazy(() => import("./pages/RealWeddingPage").then(m => ({ default: m.RealWeddingPage })));
const PublicWeddingWebsitePage = lazy(() => import("./pages/PublicWeddingWebsitePage").then(m => ({ default: m.PublicWeddingWebsitePage })));
const PublicWeddingDayPage = lazy(() => import("./pages/PublicWeddingDayPage").then(m => ({ default: m.PublicWeddingDayPage })));
const PublicWeddingRsvpPage = lazy(() => import("./pages/PublicWeddingRsvpPage").then(m => ({ default: m.PublicWeddingRsvpPage })));
const PublicWeddingAlbumPage = lazy(() => import("./pages/PublicWeddingAlbumPage").then(m => ({ default: m.PublicWeddingAlbumPage })));
const ShareMoodboardPage = lazy(() => import("./pages/ShareMoodboardPage").then(m => ({ default: m.ShareMoodboardPage })));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const NotFound = lazy(() => import("./pages/NotFound"));
const GoRedirect = lazy(() => import("./pages/GoRedirect"));
const FeedbackPage = lazy(() => import("./pages/FeedbackPage").then(m => ({ default: m.FeedbackPage })));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const DataDeletion = lazy(() => import("./pages/DataDeletion"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Policies = lazy(() => import("./pages/Policies"));
const BookingPolicy = lazy(() => import("./pages/BookingPolicy"));
const CommunityPolicy = lazy(() => import("./pages/CommunityPolicy"));
const SupplierTerms = lazy(() => import("./pages/SupplierTerms"));
const VenueTerms = lazy(() => import("./pages/VenueTerms"));
const DemoPage = lazy(() => import("./pages/DemoPage"));
const FeaturesAndPricing = lazy(() => import("./pages/FeaturesAndPricing"));
const OpenDaysPage = lazy(() => import("./pages/OpenDaysPage"));
const WeddingFairsPage = lazy(() => import("./pages/WeddingFairsPage"));
const TrialRenewalPage = lazy(() => import("./pages/TrialRenewalPage"));
const PublicQuoteView = lazy(() => import("./pages/PublicQuoteView"));
const LocationDirectory = lazy(() => import("./pages/LocationDirectory"));
const LongTailDirectory = lazy(() => import("./pages/LongTailDirectory"));
const TopListPage = lazy(() => import("./pages/TopListPage"));
const ContentHubPage = lazy(() => import("./pages/ContentHubPage"));
const WeddingHubPage = lazy(() => import("./pages/WeddingHubPage"));
const WeddingHubsIndex = lazy(() => import("./pages/WeddingHubsIndex"));
const VenueComparisonPage = lazy(() => import("./pages/VenueComparisonPage"));
const WeddingBudgetCalculator = lazy(() => import("./pages/WeddingBudgetCalculator"));
const FindWeddingVenue = lazy(() => import("./pages/FindWeddingVenue"));
const VenueMatchResults = lazy(() => import("./pages/VenueMatchResults"));
const RequestQuotes = lazy(() => import("./pages/RequestQuotes"));
const SeatingChartLanding = lazy(() => import("./pages/tools/SeatingChartLanding"));
const SeatingChartPlanner = lazy(() => import("./pages/tools/SeatingChartPlanner"));
const GuestListFeature = lazy(() => import("./pages/features/GuestListFeature"));
const SeatingPlanFeature = lazy(() => import("./pages/features/SeatingPlanFeature"));
const WeddingWebsiteFeature = lazy(() => import("./pages/features/WeddingWebsiteFeature"));
const BudgetFeature = lazy(() => import("./pages/features/BudgetFeature"));
const ChecklistFeature = lazy(() => import("./pages/features/ChecklistFeature"));
const RsvpFeature = lazy(() => import("./pages/features/RsvpFeature"));

const AppPreview = lazy(() => import("./pages/AppPreview").then(m => ({ default: m.AppPreview })));

// Business pages
const BusinessHome = lazy(() => import("./pages/business/BusinessHome").then(m => ({ default: m.BusinessHome })));
const BusinessPricing = lazy(() => import("./pages/business/BusinessPricing").then(m => ({ default: m.BusinessPricing })));
const VenuePricing = lazy(() => import("./pages/business/VenuePricing").then(m => ({ default: m.VenuePricing })));
const BusinessLogin = lazy(() => import("./pages/business/BusinessLogin").then(m => ({ default: m.BusinessLogin })));
const VenueSignup = lazy(() => import("./pages/business/VenueSignup").then(m => ({ default: m.VenueSignup })));
const VenueGroupSignup = lazy(() => import("./pages/business/VenueGroupSignup").then(m => ({ default: m.VenueGroupSignup })));
const SupplierSignup = lazy(() => import("./pages/business/SupplierSignup").then(m => ({ default: m.SupplierSignup })));
const ClaimListing = lazy(() => import("./pages/business/ClaimListing").then(m => ({ default: m.ClaimListing })));
const CRMProPage = lazy(() => import("./pages/business/CRMProPage"));
const ListYourVenue = lazy(() => import("./pages/business/ListYourVenue"));
const AdvertiseWeddingBusiness = lazy(() => import("./pages/business/AdvertiseWeddingBusiness"));
const SupplierLanding = lazy(() => import("./pages/business/SupplierLanding"));
const VenueLanding = lazy(() => import("./pages/business/VenueLanding"));
const PlanEverythingLanding = lazy(() => import("./pages/landing/PlanEverythingLanding"));
const FindSuppliersLanding = lazy(() => import("./pages/landing/FindSuppliersLanding"));
const FreeToolsLanding = lazy(() => import("./pages/landing/FreeToolsLanding"));

// Supplier pages
const SupplierDashboard = lazy(() => import("./pages/supplier/SupplierDashboard").then(m => ({ default: m.SupplierDashboard })));
const EditSupplierProfile = lazy(() => import("./pages/supplier/EditSupplierProfile").then(m => ({ default: m.EditSupplierProfile })));
const EnquiriesPage = lazy(() => import("./pages/supplier/EnquiriesPage").then(m => ({ default: m.EnquiriesPage })));
const ReviewsPage = lazy(() => import("./pages/supplier/ReviewsPage").then(m => ({ default: m.ReviewsPage })));
const SupplierOnboarding = lazy(() => import("./pages/supplier/SupplierOnboarding").then(m => ({ default: m.SupplierOnboarding })));
const SupplierQuickSetup = lazy(() => import("./pages/supplier/SupplierQuickSetup").then(m => ({ default: m.SupplierQuickSetup })));
const SupplierConcierge = lazy(() => import("./pages/supplier/SupplierConcierge").then(m => ({ default: m.SupplierConcierge })));
const AdminConcierge = lazy(() => import("./pages/admin/AdminConcierge"));
const SupplierROI = lazy(() => import("./pages/supplier/SupplierROI"));
const LostLeadsInsights = lazy(() => import("./pages/supplier/LostLeadsInsights"));
const SupplierAnalytics = lazy(() => import("./pages/supplier/SupplierAnalytics"));
const SupplierPricing = lazy(() => import("./pages/supplier/SupplierPricing"));
const CouplesActivity = lazy(() => import("./pages/supplier/CouplesActivity"));
const GrowYourBusiness = lazy(() => import("./pages/supplier/GrowYourBusiness"));
const CompetitorRankings = lazy(() => import("./pages/supplier/CompetitorRankings"));
const ImproveProfile = lazy(() => import("./pages/supplier/ImproveProfile"));
const SupplierReferrals = lazy(() => import("./pages/supplier/SupplierReferrals"));
const CouplesManager = lazy(() => import("./pages/supplier/CouplesManager").then(m => ({ default: m.CouplesManager })));
const SubscriptionManagement = lazy(() => import("./pages/supplier/SubscriptionManagement").then(m => ({ default: m.SubscriptionManagement })));
const BillingReturn = lazy(() => import("./pages/billing/BillingReturn"));
const SupplierSettings = lazy(() => import("./pages/supplier/SupplierSettings"));
const QuoteTemplatesPage = lazy(() => import("./pages/supplier/QuoteTemplatesPage"));
const PipelineStagesPage = lazy(() => import("./pages/supplier/PipelineStagesPage"));
const SupplierBadges = lazy(() => import("./pages/supplier/SupplierBadges").then(m => ({ default: m.SupplierBadges })));
const EventRsvpsPage = lazy(() => import("./pages/supplier/EventRsvpsPage"));
const PublicEventRsvpPage = lazy(() => import("./pages/PublicEventRsvpPage"));
const PublicEventRsvpByTokenPage = lazy(() => import("./pages/PublicEventRsvpByTokenPage"));

// Venue pages
const VenueDashboard = lazy(() => import("./pages/venue/VenueDashboard").then(m => ({ default: m.VenueDashboard })));
const EditVenueProfile = lazy(() => import("./pages/venue/EditVenueProfile").then(m => ({ default: m.EditVenueProfile })));
const VenueDiscountsPage = lazy(() => import("./pages/venue/VenueDiscountsPage").then(m => ({ default: m.VenueDiscountsPage })));
const ShowroundSchedulerPage = lazy(() => import("./pages/venue/ShowroundSchedulerPage").then(m => ({ default: m.ShowroundSchedulerPage })));
const VenueBadges = lazy(() => import("./pages/venue/VenueBadges").then(m => ({ default: m.VenueBadges })));
const VenueSubscriptionManagement = lazy(() => import("./pages/venue/VenueSubscriptionManagement").then(m => ({ default: m.VenueSubscriptionManagement })));
const VenueGroupDashboard = lazy(() => import("./pages/venue/VenueGroupDashboard").then(m => ({ default: m.VenueGroupDashboard })));

// Couple pages
const CoupleDashboard = lazy(() => import("./pages/couple/CoupleDashboard").then(m => ({ default: m.CoupleDashboard })));
const BookSupplierPage = lazy(() => import("./pages/couple/BookSupplierPage").then(m => ({ default: m.BookSupplierPage })));
const CoupleBookingsPage = lazy(() => import("./pages/couple/CoupleBookingsPage").then(m => ({ default: m.CoupleBookingsPage })));
const GuestsPage = lazy(() => import("./pages/couple/GuestsPage").then(m => ({ default: m.GuestsPage })));
const ChecklistPage = lazy(() => import("./pages/couple/ChecklistPage").then(m => ({ default: m.ChecklistPage })));
const BudgetPage = lazy(() => import("./pages/couple/BudgetPage").then(m => ({ default: m.BudgetPage })));
const AppBudgetScreen = lazy(() => import("./pages/couple/app/AppBudgetScreen").then(m => ({ default: m.AppBudgetScreen })));
const NotesPage = lazy(() => import("./pages/couple/NotesPage").then(m => ({ default: m.NotesPage })));
const SavedSuppliersPage = lazy(() => import("./pages/couple/SavedSuppliersPage").then(m => ({ default: m.SavedSuppliersPage })));
const EditWeddingPage = lazy(() => import("./pages/couple/EditWeddingPage").then(m => ({ default: m.EditWeddingPage })));
const WeddingSuppliersPage = lazy(() => import("./pages/couple/WeddingSuppliersPage").then(m => ({ default: m.WeddingSuppliersPage })));
const WeddingWebsitePage = lazy(() => import("./pages/couple/WeddingWebsitePage").then(m => ({ default: m.WeddingWebsitePage })));
const CreateWeddingWebsitePage = lazy(() => import("./pages/couple/CreateWeddingWebsitePage").then(m => ({ default: m.CreateWeddingWebsitePage })));
const OnboardingPage = lazy(() => import("./pages/couple/OnboardingPage").then(m => ({ default: m.OnboardingPage })));
const SeatingChart = lazy(() => import("./pages/couple/SeatingChart").then(m => ({ default: m.SeatingChart })));
const MoodboardPage = lazy(() => import("./pages/couple/MoodboardPage").then(m => ({ default: m.MoodboardPage })));
const BridesLoungePage = lazy(() => import("./pages/couple/BridesLoungePage").then(m => ({ default: m.BridesLoungePage })));
const PostDetailPage = lazy(() => import("./pages/couple/PostDetailPage").then(m => ({ default: m.PostDetailPage })));
const GuestAddressRequestPage = lazy(() => import("./pages/GuestAddressRequestPage"));
const RegistryLinksPage = lazy(() => import("./pages/couple/RegistryLinksPage").then(m => ({ default: m.RegistryLinksPage })));
const PricingEstimatorPage = lazy(() => import("./pages/couple/PricingEstimatorPage").then(m => ({ default: m.PricingEstimatorPage })));
const AIAssistantPage = lazy(() => import("./pages/couple/AIAssistantPage").then(m => ({ default: m.AIAssistantPage })));
const ExplorePage = lazy(() => import("./pages/couple/ExplorePage").then(m => ({ default: m.ExplorePage })));
const ShortlistPage = lazy(() => import("./pages/couple/ShortlistPage").then(m => ({ default: m.ShortlistPage })));
const WeddingBuilderPage = lazy(() => import("./pages/couple/WeddingBuilderPage").then(m => ({ default: m.WeddingBuilderPage })));
const PlannerPage = lazy(() => import("./pages/couple/PlannerPage").then(m => ({ default: m.PlannerPage })));
const ProfilePage = lazy(() => import("./pages/couple/ProfilePage").then(m => ({ default: m.ProfilePage })));
const SettingsPage = lazy(() => import("./pages/couple/SettingsPage"));
const WeddingDayAdminPage = lazy(() => import("./pages/couple/WeddingDayAdminPage").then(m => ({ default: m.WeddingDayAdminPage })));
const TimelinePage = lazy(() => import("./pages/couple/TimelinePage").then(m => ({ default: m.TimelinePage })));
const TravelAccommodationPage = lazy(() => import("./pages/couple/TravelAccommodationPage"));
const EventsPage = lazy(() => import("./pages/couple/EventsPage").then(m => ({ default: m.EventsPage })));
const CulturalGuidesPage = lazy(() => import("./pages/couple/CulturalGuidesPage").then(m => ({ default: m.CulturalGuidesPage })));
const WeatherForecastPage = lazy(() => import("./pages/couple/WeatherForecastPage").then(m => ({ default: m.WeatherForecastPage })));
const SupplierPortalsPage = lazy(() => import("./pages/couple/SupplierPortalsPage"));
const CulturalHubPage = lazy(() => import("./pages/CulturalHubPage").then(m => ({ default: m.CulturalHubPage })));
const CulturalAttireHubPage = lazy(() => import("./pages/CulturalAttireHubPage").then(m => ({ default: m.CulturalAttireHubPage })));
const SupplierPortalViewPage = lazy(() => import("./pages/couple/SupplierPortalViewPage"));
const CoplannersPage = lazy(() => import("./pages/couple/CoplannersPage"));
const AcceptPartnerInvitePage = lazy(() => import("./pages/AcceptPartnerInvitePage"));
const AcceptTeamInvitePage = lazy(() => import("./pages/AcceptTeamInvitePage"));
const TeamAccessPage = lazy(() => import("./pages/supplier/TeamAccessPage"));
const UnsubscribeUpdatesPage = lazy(() => import("./pages/guest/UnsubscribeUpdatesPage"));
const WeeklyDigestPage = lazy(() => import("./pages/couple/WeeklyDigestPage"));

// Messages
const MessagesPage = lazy(() => import("./pages/messages/MessagesPage").then(m => ({ default: m.MessagesPage })));
const MyEnquiriesPage = lazy(() => import("./pages/couple/MyEnquiriesPage").then(m => ({ default: m.MyEnquiriesPage })));
const WeddingInboxPage = lazy(() => import("./pages/couple/WeddingInboxPage"));

// Admin pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminSuppliers = lazy(() => import("./pages/admin/AdminSuppliers"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminRealWeddings = lazy(() => import("./pages/admin/AdminRealWeddings"));
const AdminFinance = lazy(() => import("./pages/admin/AdminFinance"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminPromoCampaignReport = lazy(() => import("./pages/admin/AdminPromoCampaignReport"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminFeedback = lazy(() => import("./pages/admin/AdminFeedback"));
const AdminVenueTermsAcceptances = lazy(() => import("./pages/admin/AdminVenueTermsAcceptances"));
const AdminSupplierPerformance = lazy(() => import("./pages/admin/AdminSupplierPerformance"));
const AdminPremiumFeatures = lazy(() => import("./pages/admin/AdminPremiumFeatures").then(m => ({ default: m.AdminPremiumFeatures })));
const AdminSubscriptionPlans = lazy(() => import("./pages/admin/AdminSubscriptionPlans"));
const AdminFeatures = lazy(() => import("./pages/admin/AdminFeatures"));
const AdminCRMSubscriptions = lazy(() => import("./pages/admin/AdminCRMSubscriptions"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));
const AdminFeaturedPlacements = lazy(() => import("./pages/admin/AdminFeaturedPlacements").then(m => ({ default: m.AdminFeaturedPlacements })));
const AdminCategoryMappings = lazy(() => import("./pages/admin/AdminCategoryMappings"));
const AdminCategoryAudit = lazy(() => import("./pages/admin/AdminCategoryAudit"));
const AdminStorage = lazy(() => import("./pages/admin/AdminStorage"));
const AdminDemoLinks = lazy(() => import("./pages/admin/AdminDemoLinks"));
const AdminGroups = lazy(() => import("./pages/admin/AdminGroups"));
const AdminInstagram = lazy(() => import("./pages/admin/AdminInstagram"));

// Pro pages
const SupplierMatchQuiz = lazy(() => import("./pages/SupplierMatchQuiz"));
const ProApp = lazy(() => import("./pages/pro/ProApp").then(m => ({ default: m.ProApp })));
const ProLogin = lazy(() => import("./pages/pro/ProLogin").then(m => ({ default: m.ProLogin })));

// Lazy-load AI widget
const ContractSignPage = lazy(() => import("./pages/ContractSignPage"));
const AIAssistantWidget = lazy(() => import("@/components/couple/AIAssistantWidget").then(m => ({ default: m.AIAssistantWidget })));

const queryClient = new QueryClient();

// Scroll to top on route change (including query string changes)
const ScrollToTop = () => {
  const { pathname, search } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname, search]);
  
  return null;
};

// Handle Capacitor native app routing
const CapacitorRedirect = () => {
  useCapacitorApp();
  return null;
};

// Minimal loading fallback
const PageFallback = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

// Inner component that has access to useLocation
const AppContent = () => {
  const location = useLocation();
  const isAIAssistantPage = location.pathname === "/couple/ai-assistant";
  const isProApp = location.pathname.startsWith("/pro");

  // When running inside the Pro native app, NEVER render the couple chrome
  // (Navbar / BottomNavigation / Footer / AI widget) - even on transient
  // routes like /auth or /reset-password - otherwise the user can get
  // visually "trapped" in the couple UI inside the Pro WebView.
  const appType = getAppType();
  const isProNativeApp = appType === "pro";
  const isNativeApp = appType === "pro" || appType === "couple";
  useNativeStatusBarOffset(appType === "couple" || appType === "pro");

  // Auth / password reset screens should be full-bleed inside ANY native
  // app - no site navbar, no footer, no bottom nav. They render their
  // own centered card layout.
  const isAuthLikeRoute =
    location.pathname === "/auth" ||
    location.pathname === "/reset-password" ||
    /^\/address-request\/?$/.test(location.pathname) ||
    location.pathname.startsWith("/auth/") ||
    // The couple onboarding wizard is full-bleed too: it draws its own progress
    // bar and safe-area padding, and the site navbar sat underneath it.
    location.pathname === "/couple/onboarding" ||
    // Private demos draw their own header (sample label, tour, exit) with no site links.
    location.pathname === "/demo";

  // Auth-like routes are always full-bleed on every platform. We hide chrome
  // synchronously on the very first render (before the lazy route chunk has
  // even resolved) so there is no navbar/footer flash during navigation.
  // The in-app browser the apps use for Google / Apple sign-in is a plain
  // website visit. While it is part way through a sign-in, show no site
  // header, footer or tabs, so it never looks like the app is now a website.
  const inAppSignInBrowser = appType === "web" && isNativeHandoffBrowser();
  const hideCoupleChrome = isProApp || isProNativeApp || isAuthLikeRoute || inAppSignInBrowser;
  // Onboarding sizes to the visible screen (dvh), not the tallest possible
  // phone viewport (vh), so short steps have no blank space to scroll into.
  const isCoupleOnboarding = location.pathname === "/couple/onboarding";

  const showAIChat =
    !isAIAssistantPage &&
    !hideCoupleChrome &&
    (location.pathname.startsWith("/couple") ||
      location.pathname === "/");

  const showFooter = !isNativeApp && !isAIAssistantPage && !hideCoupleChrome;
  const showNavbar = !isNativeApp && !hideCoupleChrome;
  const showBottomNav = !isProApp && !isProNativeApp && !isAuthLikeRoute && !inAppSignInBrowser;


  return (
    <AuthProvider>
      <QuoteBasketProvider>
      <CapacitorRedirect />
      <TrackingProvider />
      <div
        className={`${isCoupleOnboarding ? "min-h-viewport" : "min-h-screen"} flex flex-col ${isNativeApp ? "eal-native-app" : "eal-website"}`}
        data-app-type={appType}
        style={isAuthLikeRoute || isProApp || isProNativeApp ? undefined : { paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        {showNavbar && <ScrollProgress />}
        {showNavbar && <Navbar />}
        <main className={`${isProApp || isAuthLikeRoute ? "flex-1" : `flex-1 ${isNativeApp ? "pb-24" : "pb-24 md:pb-0"}`}${isFreshScope(location.pathname) ? " eal-fresh" : ""}`}>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/native-start" element={<NativeAuthStart />} />
              <Route path="/auth/native-callback" element={<NativeAuthCallback />} />
              <Route path="/instagram/done" element={<InstagramDone />} />
              <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/directory" element={<Directory />} />
              <Route path="/suppliers" element={<Navigate to={{ pathname: "/directory", search: window.location.search }} replace />} />
              <Route path="/supplier/:id" element={<SupplierProfile />} />
              <Route path="/supplier/:id/pricing" element={<SupplierPricingPage />} />
              <Route path="/event/:fairId" element={<PublicEventRsvpPage />} />
              <Route path="/event-rsvp/:token" element={<PublicEventRsvpByTokenPage />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/thank-you" element={<ThankYou />} />
              <Route path="/about" element={<About />} />
              <Route path="/help" element={<Help />} />
              <Route path="/features" element={<FeaturesAndPricing />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/open-days" element={<OpenDaysPage />} />
              <Route path="/wedding-fairs" element={<WeddingFairsPage />} />
              <Route path="/wedding-fairs-near-me" element={<WeddingFairsPage />} />
              <Route path="/trial-renewal/:token" element={<TrialRenewalPage />} />
              <Route path="/q/:token" element={<PublicQuoteView />} />
              
              {/* SEO-friendly location-based directory routes */}
              <Route path="/wedding-venues/:location" element={<LocationDirectory />} />
              <Route path="/wedding-photographers/:location" element={<LocationDirectory />} />
              <Route path="/wedding-videographers/:location" element={<LocationDirectory />} />
              <Route path="/wedding-florists/:location" element={<LocationDirectory />} />
              <Route path="/wedding-caterers/:location" element={<LocationDirectory />} />
              <Route path="/wedding-cakes/:location" element={<LocationDirectory />} />
              <Route path="/wedding-dresses/:location" element={<LocationDirectory />} />
              <Route path="/wedding-planners/:location" element={<LocationDirectory />} />
              <Route path="/wedding-hair-makeup/:location" element={<LocationDirectory />} />
              <Route path="/wedding-celebrants/:location" element={<LocationDirectory />} />
              <Route path="/wedding-transport/:location" element={<LocationDirectory />} />
              <Route path="/wedding-suits/:location" element={<LocationDirectory />} />
              <Route path="/wedding-djs/:location" element={<LocationDirectory />} />
              <Route path="/wedding-bands/:location" element={<LocationDirectory />} />
              <Route path="/wedding-decor/:location" element={<LocationDirectory />} />
              <Route path="/wedding-jewellery/:location" element={<LocationDirectory />} />
              <Route path="/wedding-jewellers/:location" element={<LocationDirectory />} />
              <Route path="/wedding-rings/:location" element={<LocationDirectory />} />
              <Route path="/wedding-entertainment/:location" element={<LocationDirectory />} />
              <Route path="/wedding-stationery/:location" element={<LocationDirectory />} />
              <Route path="/wedding-invitations/:location" element={<LocationDirectory />} />
              <Route path="/wedding-marquees/:location" element={<LocationDirectory />} />
              <Route path="/wedding-marquee-hire/:location" element={<LocationDirectory />} />
              
              {/* Long-tail SEO pages */}
              <Route path="/affordable-wedding-venues/:location" element={<LongTailDirectory />} />
              <Route path="/affordable-wedding-photographers/:location" element={<LongTailDirectory />} />
              <Route path="/luxury-wedding-venues/:location" element={<LongTailDirectory />} />
              <Route path="/luxury-wedding-photographers/:location" element={<LongTailDirectory />} />
              <Route path="/best-wedding-venues/:location" element={<LongTailDirectory />} />
              <Route path="/best-wedding-photographers/:location" element={<LongTailDirectory />} />
              <Route path="/budget-wedding-venues/:location" element={<LongTailDirectory />} />
              <Route path="/budget-wedding-photographers/:location" element={<LongTailDirectory />} />
              <Route path="/top-rated-wedding-venues/:location" element={<LongTailDirectory />} />
              <Route path="/top-rated-wedding-photographers/:location" element={<LongTailDirectory />} />
              
              {/* Top 10 listicle pages */}
              <Route path="/best/:type/:location" element={<TopListPage />} />
              
              {/* Content hub guide pages */}
              <Route path="/guide/:type/:location" element={<ContentHubPage />} />
              <Route path="/weddings" element={<WeddingHubsIndex />} />
              <Route path="/weddings/:slug" element={<WeddingHubPage />} />
              
              {/* Cultural & religious wedding hubs */}
              <Route path="/cultural/:slug" element={<CulturalHubPage />} />
              <Route path="/cultural-attire" element={<CulturalAttireHubPage />} />
              <Route path="/cultural-attire/:slug" element={<CulturalAttireHubPage />} />
              
              {/* Venue comparison pages */}
              <Route path="/compare/:venue1Slug/:venue2Slug" element={<VenueComparisonPage />} />
              
              {/* Tools pages */}
              <Route path="/tools/wedding-budget-calculator" element={<WeddingBudgetCalculator />} />
              <Route path="/tools/seating-chart-maker" element={<SeatingChartLanding />} />
              <Route path="/tools/seating-chart-maker/planner" element={<SeatingChartPlanner />} />
              <Route path="/find-a-wedding-supplier" element={<SupplierMatchQuiz />} />
              <Route path="/find-a-wedding-venue" element={<FindWeddingVenue />} />
              <Route path="/venue-match" element={<VenueMatchResults />} />
              <Route path="/request-quotes" element={<RequestQuotes />} />

              {/* Free feature pages */}
              <Route path="/features/guest-list" element={<GuestListFeature />} />
              <Route path="/features/seating-plan" element={<SeatingPlanFeature />} />
              <Route path="/features/wedding-website" element={<WeddingWebsiteFeature />} />
              <Route path="/features/budget" element={<BudgetFeature />} />
              <Route path="/features/checklist" element={<ChecklistFeature />} />
              <Route path="/features/rsvps" element={<RsvpFeature />} />

              <Route path="/app-preview" element={<AppPreview />} />
              
              {/* B2B landing pages for SEO */}
              <Route path="/list-your-venue" element={<ListYourVenue />} />
              <Route path="/advertise-wedding-business" element={<AdvertiseWeddingBusiness />} />
              <Route path="/for-suppliers" element={<SupplierLanding />} />
              <Route path="/for-venues" element={<VenueLanding />} />
              <Route path="/plan-everything" element={<PlanEverythingLanding />} />
              <Route path="/find-suppliers" element={<FindSuppliersLanding />} />
              <Route path="/free-tools" element={<FreeToolsLanding />} />
              
              <Route path="/business" element={<BusinessHome />} />
              <Route path="/business/pricing" element={<WebBillingOnly><BusinessPricing /></WebBillingOnly>} />
              <Route path="/business/venue/pricing" element={<WebBillingOnly><VenuePricing /></WebBillingOnly>} />
              <Route path="/business/login" element={<BusinessLogin />} />
              <Route path="/business/venue/signup" element={<VenueSignup />} />
              <Route path="/business/venue/signup/group" element={<VenueGroupSignup />} />
              <Route path="/business/signup/group" element={<VenueGroupSignup />} />
              <Route path="/business/supplier/signup" element={<SupplierSignup />} />
              <Route path="/business/crm-pro" element={<CRMProPage />} />
              <Route path="/business/claim/:supplierId" element={<ClaimListing />} />
              <Route path="/pro/login" element={<ProLogin />} />
              <Route path="/pro/*" element={<BusinessProtectedRoute><ProApp /></BusinessProtectedRoute>} />
              <Route path="/supplier/onboarding" element={<BusinessProtectedRoute><SupplierQuickSetup /></BusinessProtectedRoute>} />
              <Route path="/supplier/onboarding/detailed" element={<BusinessProtectedRoute><SupplierOnboarding /></BusinessProtectedRoute>} />
              <Route path="/supplier/dashboard" element={<BusinessProtectedRoute>{isProNativeApp ? <Navigate to="/pro/home" replace /> : <SupplierDashboard />}</BusinessProtectedRoute>} />
              <Route path="/supplier/profile/edit" element={<BusinessProtectedRoute><EditSupplierProfile /></BusinessProtectedRoute>} />
              <Route path="/supplier/enquiries" element={<BusinessProtectedRoute><EnquiriesPage /></BusinessProtectedRoute>} />
              <Route path="/supplier/reviews" element={<BusinessProtectedRoute><ReviewsPage /></BusinessProtectedRoute>} />
              <Route path="/supplier/events/:fairId/rsvps" element={<BusinessProtectedRoute><EventRsvpsPage /></BusinessProtectedRoute>} />
              <Route path="/supplier/roi" element={<BusinessProtectedRoute><SupplierROI /></BusinessProtectedRoute>} />
              <Route path="/supplier/lost-leads" element={<BusinessProtectedRoute><LostLeadsInsights /></BusinessProtectedRoute>} />
              <Route path="/supplier/analytics" element={<BusinessProtectedRoute><SupplierAnalytics /></BusinessProtectedRoute>} />
              <Route path="/supplier/pricing" element={<BusinessProtectedRoute><SupplierPricing /></BusinessProtectedRoute>} />
              <Route path="/supplier/couples-activity" element={<BusinessProtectedRoute><CouplesActivity /></BusinessProtectedRoute>} />
              <Route path="/supplier/grow-business" element={<BusinessProtectedRoute><GrowYourBusiness /></BusinessProtectedRoute>} />
              <Route path="/supplier/competitor-rankings" element={<BusinessProtectedRoute><CompetitorRankings /></BusinessProtectedRoute>} />
              <Route path="/supplier/improve-profile" element={<BusinessProtectedRoute><ImproveProfile /></BusinessProtectedRoute>} />
              <Route path="/supplier/badges" element={<BusinessProtectedRoute><SupplierBadges /></BusinessProtectedRoute>} />
              <Route path="/supplier/settings" element={<BusinessProtectedRoute><SupplierSettings /></BusinessProtectedRoute>} />
              <Route path="/supplier/quote-templates" element={<BusinessProtectedRoute><QuoteTemplatesPage /></BusinessProtectedRoute>} />
              <Route path="/supplier/pipeline-stages" element={<BusinessProtectedRoute><PipelineStagesPage /></BusinessProtectedRoute>} />
              <Route path="/supplier/referrals" element={<BusinessProtectedRoute><SupplierReferrals /></BusinessProtectedRoute>} />
              <Route path="/supplier/couples" element={<BusinessProtectedRoute><CouplesManager /></BusinessProtectedRoute>} />
              <Route path="/venue/couples" element={<BusinessProtectedRoute><CouplesManager /></BusinessProtectedRoute>} />
              <Route path="/venue/dashboard" element={<BusinessProtectedRoute>{isProNativeApp ? <Navigate to="/pro/home" replace /> : <VenueDashboard />}</BusinessProtectedRoute>} />
              <Route path="/venue/profile/edit" element={<BusinessProtectedRoute><EditVenueProfile /></BusinessProtectedRoute>} />
              <Route path="/venue/badges" element={<BusinessProtectedRoute><VenueBadges /></BusinessProtectedRoute>} />
              <Route path="/venue/discounts" element={<BusinessProtectedRoute><VenueDiscountsPage /></BusinessProtectedRoute>} />
              <Route path="/venue/showrounds" element={<BusinessProtectedRoute><ShowroundSchedulerPage /></BusinessProtectedRoute>} />
              <Route path="/venue/subscription" element={<BusinessProtectedRoute><WebBillingOnly><VenueSubscriptionManagement /></WebBillingOnly></BusinessProtectedRoute>} />
              <Route path="/venue/group" element={<BusinessProtectedRoute><VenueGroupDashboard /></BusinessProtectedRoute>} />
              <Route path="/venue/venues" element={<BusinessProtectedRoute><VenueGroupDashboard /></BusinessProtectedRoute>} />
              <Route path="/couple/dashboard" element={<ProtectedRoute><CoupleDashboard /></ProtectedRoute>} />
              <Route path="/couple/bookings" element={<ProtectedRoute><CoupleBookingsPage /></ProtectedRoute>} />
              <Route path="/couple/book/:supplierId" element={<ProtectedRoute><BookSupplierPage /></ProtectedRoute>} />
              <Route path="/couple/explore" element={<ProtectedRoute><ExplorePage /></ProtectedRoute>} />
              <Route path="/couple/shortlist" element={<ProtectedRoute><ShortlistPage /></ProtectedRoute>} />
              <Route path="/couple/build" element={<ProtectedRoute><WeddingBuilderPage /></ProtectedRoute>} />
              <Route path="/couple/planner" element={<ProtectedRoute><PlannerPage /></ProtectedRoute>} />
              <Route path="/couple/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="/couple/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
              <Route path="/couple/co-planners" element={<ProtectedRoute><CoplannersPage /></ProtectedRoute>} />
              <Route path="/invite/partner/:token" element={<AcceptPartnerInvitePage />} />
              <Route path="/contract/sign/:token" element={<ContractSignPage />} />
              <Route path="/team/accept/:token" element={<AcceptTeamInvitePage />} />
              <Route path="/supplier/team-access" element={<TeamAccessPage />} />
              <Route path="/guest/unsubscribe/:token" element={<UnsubscribeUpdatesPage />} />
              <Route path="/couple/enquiries" element={<ProtectedRoute><MyEnquiriesPage /></ProtectedRoute>} />
              <Route path="/couple/inbox" element={<ProtectedRoute><WeddingInboxPage /></ProtectedRoute>} />
              <Route path="/dashboard/inbox" element={<ProtectedRoute><WeddingInboxPage /></ProtectedRoute>} />
              <Route path="/couple/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
              <Route path="/couple/guests" element={<ProtectedRoute><GuestsPage /></ProtectedRoute>} />
              <Route path="/couple/checklist" element={<ProtectedRoute><ChecklistPage /></ProtectedRoute>} />
<Route path="/couple/budget" element={<ProtectedRoute>{appType === "couple" ? <AppBudgetScreen /> : <BudgetPage />}</ProtectedRoute>} />
              <Route path="/couple/digest/latest" element={<ProtectedRoute><WeeklyDigestPage /></ProtectedRoute>} />
              <Route path="/couple/digest/:digestId" element={<ProtectedRoute><WeeklyDigestPage /></ProtectedRoute>} />
              <Route path="/couple/notes" element={<ProtectedRoute><NotesPage /></ProtectedRoute>} />
              <Route path="/couple/saved-suppliers" element={<ProtectedRoute><SavedSuppliersPage /></ProtectedRoute>} />
              <Route path="/couple/wedding/edit" element={<ProtectedRoute><EditWeddingPage /></ProtectedRoute>} />
              <Route path="/couple/suppliers" element={<ProtectedRoute><WeddingSuppliersPage /></ProtectedRoute>} />
              <Route path="/couple/website" element={<ProtectedRoute><WeddingWebsitePage /></ProtectedRoute>} />
              <Route path="/couple/website/create" element={<ProtectedRoute><CreateWeddingWebsitePage /></ProtectedRoute>} />
              <Route path="/couple/seating" element={<ProtectedRoute><SeatingChart /></ProtectedRoute>} />
              <Route path="/couple/seating-chart" element={<ProtectedRoute><SeatingChart /></ProtectedRoute>} />
              <Route path="/couple/moodboard" element={<ProtectedRoute><MoodboardPage /></ProtectedRoute>} />
              <Route path="/couple/brides-lounge" element={<ProtectedRoute><BridesLoungePage /></ProtectedRoute>} />
              <Route path="/couple/brides-lounge/:postId" element={<ProtectedRoute><PostDetailPage /></ProtectedRoute>} />
              <Route path="/address-request" element={<GuestAddressRequestPage key={`${location.key}:${location.hash}`} />} />
              <Route path="/couple/registry-links" element={<ProtectedRoute><RegistryLinksPage /></ProtectedRoute>} />
              <Route path="/couple/pricing-estimator" element={<PricingEstimatorPage />} />
              <Route path="/couple/wedding-day" element={<ProtectedRoute><WeddingDayAdminPage /></ProtectedRoute>} />
              <Route path="/couple/timeline" element={<ProtectedRoute><TimelinePage /></ProtectedRoute>} />
              <Route path="/couple/travel" element={<ProtectedRoute><TravelAccommodationPage /></ProtectedRoute>} />
              <Route path="/couple/ai-assistant" element={<ProtectedRoute><AIAssistantPage /></ProtectedRoute>} />
              <Route path="/couple/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
              <Route path="/couple/cultural-guides" element={<ProtectedRoute><CulturalGuidesPage /></ProtectedRoute>} />
              <Route path="/couple/weather" element={<ProtectedRoute><WeatherForecastPage /></ProtectedRoute>} />
              <Route path="/couple/portals" element={<ProtectedRoute><SupplierPortalsPage /></ProtectedRoute>} />
              <Route path="/couple/portal/:portalId" element={<SupplierPortalViewPage />} />
              <Route path="/portal/:portalId" element={<SupplierPortalViewPage />} />
              <Route path="/inspiration" element={<InspirationPage />} />
              <Route path="/submit-wedding" element={<SubmitWeddingPage />} />
              <Route path="/real-wedding/:id" element={<RealWeddingPage />} />
              <Route path="/wedding/:id" element={<PublicWeddingWebsitePage />} />
              <Route path="/wedding/:id/guest" element={<PublicWeddingWebsitePage />} />
              <Route path="/wedding/:id/day" element={<PublicWeddingDayPage />} />
              <Route path="/wedding/:id/rsvp" element={<PublicWeddingRsvpPage />} />
              <Route path="/wedding/:id/album" element={<PublicWeddingAlbumPage />} />
              <Route path="/share/moodboard/:shareToken" element={<ShareMoodboardPage />} />
              <Route path="/messages" element={<MessagesPage />} />
              <Route path="/feedback" element={<FeedbackPage />} />
              <Route path="/admin" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
              <Route path="/admin/suppliers" element={<AdminProtectedRoute><AdminSuppliers /></AdminProtectedRoute>} />
              <Route path="/admin/users" element={<AdminProtectedRoute><AdminUsers /></AdminProtectedRoute>} />
              <Route path="/admin/reviews" element={<AdminProtectedRoute><AdminReviews /></AdminProtectedRoute>} />
              <Route path="/admin/real-weddings" element={<AdminProtectedRoute><AdminRealWeddings /></AdminProtectedRoute>} />
              <Route path="/admin/finance" element={<AdminProtectedRoute><AdminFinance /></AdminProtectedRoute>} />
              <Route path="/admin/reports" element={<AdminProtectedRoute><AdminReports /></AdminProtectedRoute>} />
              <Route path="/admin/reports/promo-campaigns" element={<AdminProtectedRoute><AdminPromoCampaignReport /></AdminProtectedRoute>} />
              <Route path="/admin/blog" element={<AdminProtectedRoute><AdminBlog /></AdminProtectedRoute>} />
              <Route path="/admin/feedback" element={<AdminProtectedRoute><AdminFeedback /></AdminProtectedRoute>} />
              <Route path="/admin/venue-terms" element={<AdminProtectedRoute><AdminVenueTermsAcceptances /></AdminProtectedRoute>} />
              <Route path="/admin/performance" element={<AdminProtectedRoute><AdminSupplierPerformance /></AdminProtectedRoute>} />
              <Route path="/admin/premium-features" element={<AdminProtectedRoute><AdminPremiumFeatures /></AdminProtectedRoute>} />
              <Route path="/admin/subscriptions" element={<AdminProtectedRoute><AdminSubscriptionPlans /></AdminProtectedRoute>} />
              <Route path="/admin/features" element={<AdminProtectedRoute><AdminFeatures /></AdminProtectedRoute>} />
              <Route path="/admin/featured-placements" element={<AdminProtectedRoute><AdminFeaturedPlacements /></AdminProtectedRoute>} />
              <Route path="/admin/category-mappings" element={<AdminProtectedRoute><AdminCategoryMappings /></AdminProtectedRoute>} />
              <Route path="/admin/category-audit" element={<AdminProtectedRoute><AdminCategoryAudit /></AdminProtectedRoute>} />
              <Route path="/admin/crm-subscriptions" element={<AdminProtectedRoute><AdminCRMSubscriptions /></AdminProtectedRoute>} />
              <Route path="/admin/messages" element={<AdminProtectedRoute><AdminMessages /></AdminProtectedRoute>} />
              <Route path="/admin/concierge" element={<AdminProtectedRoute><AdminConcierge /></AdminProtectedRoute>} />
              <Route path="/admin/storage" element={<AdminProtectedRoute><AdminStorage /></AdminProtectedRoute>} />
              <Route path="/admin/demos" element={<AdminProtectedRoute><AdminDemoLinks /></AdminProtectedRoute>} />
              <Route path="/admin/groups" element={<AdminProtectedRoute><AdminGroups /></AdminProtectedRoute>} />
              <Route path="/admin/instagram" element={<AdminProtectedRoute><AdminInstagram /></AdminProtectedRoute>} />
              <Route path="/supplier/subscription" element={<BusinessProtectedRoute><WebBillingOnly><SubscriptionManagement /></WebBillingOnly></BusinessProtectedRoute>} />
              <Route path="/billing/return" element={<BusinessProtectedRoute><WebBillingOnly><BillingReturn /></WebBillingOnly></BusinessProtectedRoute>} />

              <Route path="/supplier/concierge" element={<BusinessProtectedRoute><SupplierConcierge /></BusinessProtectedRoute>} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/data-deletion" element={<DataDeletion />} />
              <Route path="/policies" element={<Policies />} />
              <Route path="/booking-policy" element={<BookingPolicy />} />
              <Route path="/community-policy" element={<CommunityPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/supplier-terms" element={<SupplierTerms />} />
              <Route path="/business/terms" element={<SupplierTerms />} />
              <Route path="/venue-terms" element={<VenueTerms />} />
              <Route path="/business/venue-terms" element={<VenueTerms />} />
              <Route path="/demo" element={<DemoPage />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="/go/:code" element={<GoRedirect />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        {showFooter && <Footer />}
        {showAIChat && (
          <Suspense fallback={null}>
            <AIAssistantWidget />
          </Suspense>
        )}
        {showFooter && <BackToTop />}
        <QuoteBasketBar />
        {showBottomNav && <BottomNavigation />}
        {showBottomNav && !isNativeApp && <StickyMobileCTA />}
      </div>
      </QuoteBasketProvider>
    </AuthProvider>
  );
};

const App = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <NativeHandoffBrowserGuard />
            <PushNavigationBridge />
            <AppContent />
            <CookieConsent />
            <AttachmentPreviewHost />
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
